import React, { useCallback, useEffect, useState } from 'react';
import toast from '../../../utils/toast';
import { Pill } from '../dashboard/dashboard-units-components/dashboard-units.styled';
import {
  ShiftCard,
  CardHead,
  CardTitle,
  CardNote,
  Group,
  GroupTitle,
  PersonRow,
  PersonName,
  PersonMeta,
  Mono,
  EmptyLine,
  Caveat,
  Actions,
  SecondaryAction,
} from '../../shifts/shifts.styled';
import { getShiftMonitor } from '../../../api/shifts-client';
import {
  ShiftMonitorResponse,
  MonitorSessionPerson,
} from '../../../interfaces/shift-interfaces';
import {
  ageLabel,
  clockNote,
  clockTime,
  elapsed,
} from '../../../function-utils/building-time';
import { useClock } from '../../../context/clock-context';
import { ClockNote } from '../../clock-note/clock-note';

/**
 * WHO IS ON THE DESK RIGHT NOW — the three groups, and nothing around them.
 *
 * ═══════════════ WHY THIS IS A PANEL AND NOT A PAGE ═══════════════
 * It was the whole of /shift-monitor. The Shifts tab in the dashboard now needs
 * the same three groups as its "On now" view, and the alternative — a second
 * implementation over there — would have duplicated the three CAVEATS below,
 * each of which took a written decision to word. A caveat that exists on one of
 * two screens is a caveat that will be missing from the one somebody reads.
 *
 * So the page chrome (title, back, the surrounding layout) stayed behind and
 * this is only the content. /shift-monitor now redirects into the tab.
 *
 * ═══════════════ NOT AN AI SURFACE ═══════════════
 * No aurora anywhere in it. It reads `shifts` and `sessions`; nothing here is
 * model-written, and this view must keep working with ai_config.enabled = false.
 *
 * ═══════════════ THE THREE RENDERING CONSTRAINTS ═══════════════
 * They are written into the backend (lobbero_be/src/CRUD/shifts.js) and they are
 * not style preferences — each is the difference between a vague truth and a
 * precise lie about a named employee:
 *
 *  1. `logged_in_not_clocked` is ASSIGNMENT-BASED. One manager assigned to six
 *     buildings and logged in once appears in all six lists from ONE session.
 *     Never label it "present"; never sum it across buildings.
 *  2. `last_used_at` renders as an AGE, never a binary dot. The liveness window
 *     is 15 minutes, so somebody who shut a laptop 10 minutes ago still appears.
 *     An age says that out loud; a green dot hides it.
 *  3. `logged_in_unassigned` — typically the org owner, who holds no
 *     access_scope rows — is rendered ONCE and never attributed to a building.
 */

const COPY = {
  refresh: 'Refresh',
  loading: 'Reading the desk…',
  failed: 'Could not read the shift monitor.',

  clockedTitle: 'Clocked in',
  clockedEmpty: 'Nobody is clocked in at this building.',
  clockedNote: 'Straight off the attendance record.',

  assignedTitle: 'Assigned here and logged in, not clocked in',
  assignedEmpty: 'Nobody assigned here has an active session.',
  assignedNote:
    'This is an assignment list, not a headcount, and not a statement that these people are in the building. One person assigned to several buildings appears in every one of their lists from a single login — so it must never be added up across buildings.',
  ageNote:
    'Ages come from the last time each session was used. The window is 15 minutes, so somebody who closed a laptop a few minutes ago still appears here.',

  unassignedTitle: 'Logged in, assigned to no building',
  unassignedEmpty: 'Nobody.',
  unassignedNote:
    'Usually the account owner, who covers the whole organisation and therefore holds no per-building assignment. Shown once, never attributed to a building.',

  unknownName: 'Name unavailable',
  since: 'since',
  asOf: 'as of',
  noBuilding: 'Choose a building to read its desk.',

  // THE CLOCK IS THE BUILDING'S, AND IT IS NAMED. A clock-in time rendered in
  // the reader's browser zone and the same clock-in time rendered in the
  // building's — which is what the Shifts tab beside this panel has always
  // done — are the same instant with two different faces, and a manager
  // covering a desk two zones away has no way to tell which one they are
  // looking at unless the zone is written down.
};

const SessionList: React.FC<{ people: MonitorSessionPerson[] }> = ({
  people,
}) => (
  <>
    {people.map(p => (
      <PersonRow key={p.user_id}>
        <PersonName>{p.name || COPY.unknownName}</PersonName>
        {/* AN AGE, NEVER A DOT. See constraint 2 in the header. */}
        <PersonMeta>{ageLabel(p.last_used_at)}</PersonMeta>
      </PersonRow>
    ))}
  </>
);

interface Props {
  /** Null when no building is chosen yet. The monitor is per building by
   *  construction (`?building_id=`), so this is a real state and not a loading
   *  one — the panel says so rather than showing an empty desk. */
  buildingId: number | null;
  buildingName?: string;
  /** The building's own IANA zone, when the host holds it. NOT the clock the
   *  panel is on: this panel lives on the manager's dashboard, so it is on the
   *  READER's clock like the history rows beside it (R-T12) — one screen, one
   *  clock, named. The building's zone only matters if this panel is ever
   *  drawn on a desk surface, where useClock would answer it instead. */
  buildingTimezone?: string | null;
  /** The host's own building picker, rendered inside the panel's header so the
   *  control sits where the reader is already looking. Optional: the standalone
   *  page had none, because the app shell chose the building for it. */
  buildingPicker?: React.ReactNode;
}

export const ShiftMonitorPanel: React.FC<Props> = ({
  buildingId,
  buildingName,
  buildingTimezone,
  buildingPicker,
}) => {
  const [data, setData] = useState<ShiftMonitorResponse | null>(null);
  const [loading, setLoading] = useState(false);
  // The moment the numbers below were true. Ages are computed once, at render,
  // and there is no timer refreshing them — so the view says WHEN it was
  // accurate rather than letting a stale "active 2 min ago" quietly age on a
  // screen nobody has touched for an hour.
  const [fetchedAt, setFetchedAt] = useState<Date | null>(null);

  const hasBuilding = Number.isInteger(buildingId);
  // THE CLOCK THIS PANEL IS ON: the surface's. On the dashboard that is the
  // reader's own device (an owner or a manager); it was "the building's, if
  // somebody passes it" — and nobody did, so it was the device's, unnamed.
  const clock = useClock({ buildingId, buildingZone: buildingTimezone });
  const zone = clock.zone;

  const load = useCallback(async (): Promise<void> => {
    if (!Number.isInteger(buildingId)) return;
    setLoading(true);
    try {
      const res = await getShiftMonitor(Number(buildingId));
      setData(res);
      setFetchedAt(new Date());
    } catch (e) {
      toast.error(e instanceof Error ? e.message : COPY.failed);
    } finally {
      setLoading(false);
    }
  }, [buildingId]);

  useEffect(() => {
    load();
  }, [load]);

  const clocked = data?.clocked_in || [];
  const notClocked = data?.logged_in_not_clocked || [];
  const unassigned = data?.logged_in_unassigned || [];

  return (
    <>
      {buildingPicker || hasBuilding ? (
        <ShiftCard>
          <CardHead>
            <CardTitle>{buildingName || COPY.noBuilding}</CardTitle>
            <Actions>
              <SecondaryAction
                type="button"
                disabled={loading || !hasBuilding}
                onClick={load}
              >
                {COPY.refresh}
              </SecondaryAction>
            </Actions>
          </CardHead>
          {buildingPicker}
        </ShiftCard>
      ) : null}

      {!hasBuilding ? (
        <ShiftCard>
          <EmptyLine>{COPY.noBuilding}</EmptyLine>
        </ShiftCard>
      ) : null}

      {hasBuilding && !data && loading ? (
        <ShiftCard>
          <CardNote>{COPY.loading}</CardNote>
        </ShiftCard>
      ) : null}

      {hasBuilding && data ? (
        <>
          <ShiftCard>
            <CardHead>
              <CardTitle>{COPY.clockedTitle}</CardTitle>
              <Pill tone="success">{clocked.length}</Pill>
            </CardHead>
            <Group>
              {clocked.length ? (
                clocked.map(p => (
                  <PersonRow key={p.user_id}>
                    <PersonName>{p.name || COPY.unknownName}</PersonName>
                    <Mono>
                      {`${COPY.since} ${clockTime(
                        p.started_at,
                        zone
                      )} · ${elapsed(p.started_at)}`}
                    </Mono>
                  </PersonRow>
                ))
              ) : (
                <EmptyLine>{COPY.clockedEmpty}</EmptyLine>
              )}
            </Group>
            <CardNote>{COPY.clockedNote}</CardNote>
            {/* Which clock "since 07:00" is on, always. */}
            <ClockNote clock={clock} />
          </ShiftCard>

          <ShiftCard>
            <CardHead>
              {/* The heading is the backend's sentence, unedited. */}
              <GroupTitle>{COPY.assignedTitle}</GroupTitle>
              <Pill tone="warning">{notClocked.length}</Pill>
            </CardHead>
            <Group>
              {notClocked.length ? (
                <SessionList people={notClocked} />
              ) : (
                <EmptyLine>{COPY.assignedEmpty}</EmptyLine>
              )}
            </Group>
            {/* The server ships its own caveat in the payload so a consumer
                cannot quietly treat this group as a headcount. It is rendered
                when present, with the fuller sentence beneath it. */}
            {data.note ? <CardNote>{data.note}</CardNote> : null}
            <Caveat>{COPY.assignedNote}</Caveat>
            <Caveat>{COPY.ageNote}</Caveat>
          </ShiftCard>

          <ShiftCard>
            <CardHead>
              <CardTitle>{COPY.unassignedTitle}</CardTitle>
              <Pill tone="neutral">{unassigned.length}</Pill>
            </CardHead>
            <Group>
              {unassigned.length ? (
                <SessionList people={unassigned} />
              ) : (
                <EmptyLine>{COPY.unassignedEmpty}</EmptyLine>
              )}
            </Group>
            <Caveat>{COPY.unassignedNote}</Caveat>
          </ShiftCard>

          {fetchedAt ? (
            <CardNote>
              {`${COPY.asOf} ${clockTime(fetchedAt.toISOString(), zone)} · ${clockNote(clock)}`}
            </CardNote>
          ) : null}
        </>
      ) : null}
    </>
  );
};
